// CreditHistoryTable.js (updated)
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TablePagination,
  Box,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { creditlogs } from "../../store/creditlogsSlice";
import DownloadCreditLogs from "./DownloadCreditedLogs";
import CreditHistoryChart from "./CreditHistoryChart";
import { getAccesstoken } from "../../helpers/utils";
import axios from "axios";
import { base_url } from "../../utils";

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  padding: theme.spacing(3),
  borderRadius: "12px",
  boxShadow: "0 6px 20px rgba(0, 0, 0, 0.1)",
  background: "linear-gradient(135deg, #ffffff, #f8f9fa)",
}));

const StyledChartContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  padding: theme.spacing(3),
  borderRadius: "12px",
  boxShadow: "0 8px 25px rgba(0, 0, 0, 0.15)",
  background: "linear-gradient(135deg, #ffffff, #f8f9fa)",
  maxWidth: "100%",
  marginLeft: "auto",
  marginRight: "auto",
  overflow: "hidden",
}));

const StyledTable = styled(Table)(({ theme }) => ({
  borderCollapse: "separate",
  borderSpacing: "0 8px",
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  background: "linear-gradient(90deg, #1e3c72, #2a5298, #c11c37)",
  "& th": {
    color: "white",
    fontWeight: 700,
    fontSize: "1rem",
    letterSpacing: "0.5px",
    padding: theme.spacing(2),
    borderBottom: "none",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  background: "rgba(255, 255, 255, 0.95)",
  borderRadius: "8px",
  boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
    background: "rgba(248, 249, 250, 1)",
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: "none",
  fontSize: "0.80rem",
  color: "#2a5298",
}));

const CreditHistoryTable = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("datetime");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedCount, setSelectedCount] = useState(null);
  const [myData, setMyData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const creditLogsData = useCallback(async () => {
    try {
      const token = getAccesstoken();
      const response = await axios.get(
        `${base_url}/digilocker/get_credit_logs?page=${
          page + 1
        }&per_page=${rowsPerPage}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      console.log("credit history table", response);
      setMyData(response.data.data);
      setTotalRecords(response.data.pagination.total);
    } catch (error) {
      console.error("API error:", error);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    creditLogsData();
  }, [creditLogsData]);

  const handleBarClick = (date, count) => {
    setSelectedDate(date);
    setSelectedCount(count);
    setDialogOpen(true);
  };

  const handleSort = (property) => () => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedCreditLogs = useMemo(() => {
    if (orderBy === "datetime") {
      return myData.slice().sort((a, b) => {
        const dateA = new Date(a[orderBy]);
        const dateB = new Date(b[orderBy]);
        return order === "asc" ? dateA - dateB : dateB - dateA;
      });
    }
    return myData;
  }, [myData, orderBy, order]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedDate(null);
    setSelectedCount(null);
  };

  return (
    <>
      {/* Table Section */}
      <StyledTableContainer component={Paper}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            marginTop: 2,
            marginBottom: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              marginBottom: 2,
              fontWeight: 700,
              color: "#1e3c72",
            }}
          >
            Credit Balance History
          </Typography>
          <DownloadCreditLogs />
        </Box>
        <StyledChartContainer>
          <CreditHistoryChart
            data={myData}
            onBarClick={handleBarClick}
            height={400}
          />
        </StyledChartContainer>
        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <StyledTable>
            <StyledTableHead>
              <TableRow>
                <StyledTableCell>
                  <strong>ID</strong>
                </StyledTableCell>
                <StyledTableCell>
                  <strong>Transaction ID</strong>
                </StyledTableCell>
                <StyledTableCell>
                  <strong>Digilocker Transaction</strong>
                </StyledTableCell>
                <StyledTableCell>
                  <TableSortLabel
                    active={orderBy === "datetime"}
                    direction={orderBy === "datetime" ? order : "asc"}
                    onClick={handleSort("datetime")}
                    sx={{
                      color: "white !important",
                      "&:hover": {
                        color: "rgba(255, 255, 255, 0.8) !important",
                      },
                      "& .MuiTableSortLabel-icon": {
                        color: "white !important",
                      },
                    }}
                  >
                    <strong>Date & Time</strong>
                  </TableSortLabel>
                </StyledTableCell>
                <StyledTableCell>
                  <strong>Used Credits</strong>
                </StyledTableCell>
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {sortedCreditLogs.map((log, index) => (
                <StyledTableRow
                  key={log.id}
                  sx={{
                    animation: `fadeIn 0.5s ease-in-out ${
                      index * 0.1
                    }s forwards`,
                    "@keyframes fadeIn": {
                      "0%": { opacity: 0, transform: "translateY(10px)" },
                      "100%": { opacity: 1, transform: "translateY(0)" },
                    },
                  }}
                >
                  <StyledTableCell>{log.id}</StyledTableCell>
                  <StyledTableCell>{log.transaction_id}</StyledTableCell>
                  <StyledTableCell>
                    {log.digilocker_transaction}
                  </StyledTableCell>
                  <StyledTableCell>
                    {new Date(log.datetime).toLocaleString("en-US", {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </StyledTableCell>
                  <StyledTableCell>{log.used_credits}</StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </StyledTable>
        </Box>

        <TablePagination
          rowsPerPageOptions={[5, 10]}
          component="div"
          count={totalRecords}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            "& .MuiTablePagination-toolbar": {
              padding: "4px 8px",
              fontSize: "12px",
            },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              {
                fontSize: "12px",
              },
            "@media (max-width: 600px)": {
              "& .MuiTablePagination-toolbar": {
                flexDirection: "column",
                alignItems: "center",
              },
            },
          }}
        />
      </StyledTableContainer>

      {/* Enhanced Interactive Graph using new component */}

      {/* Dialog for bar click */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{ background: "red", color: "white", fontWeight: 700 }}
        >
          Transaction Details
        </DialogTitle>
        <DialogContent sx={{ padding: 3 }}>
          <Typography variant="body1" sx={{ color: "#2a5298" }}>
            <strong>Date:</strong> {selectedDate}
          </Typography>
          <Typography variant="body1" sx={{ color: "#2a5298", mt: 1 }}>
            <strong>Transactions:</strong> {selectedCount}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            sx={{ color: "#c11c37", fontWeight: 500 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreditHistoryTable;
